import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Typography, CircleLoader } from '@hpstellar/core';
import { Search, Cross } from '@hpstellar/icons';
import debounce from 'lodash/debounce';

import AutocompleteContainer from './autocomplete-container';

import useHawksearchClientData from '../../search/useHawksearchClientData';
import useSearchEndpoint from '../../search/useSearchEndpoint';
import useSuppliesSearch from '../useSuppliesSearch';
import { autoCompleteAction } from '../util';

import './supplies-search.less';

export default function SuppliesSearch({
    defaultSearch = '',
    productGroups,
    autoCompleteResults,
    autocompleteCount = 6,
}) {
    const [searchApi] = useSearchEndpoint('suppliesfinder');
    const clientData = useHawksearchClientData();
    const { search, isLoading, clearResults, setLatestSearch } = useSuppliesSearch();

    const [query, setQuery] = useState(defaultSearch);
    const searchInput = useRef(null);
    const [results, setResults] = useState(autoCompleteResults);

    const handleChange = value => {
        setQuery(value);
        if (value === '') {
            setResults(null);
            clearResults();
        }
    };

    const debouncedSearch = useMemo(() => debounce(handleChange, 200), []);

    const onClearSearch = useCallback(() => {
        setQuery('');
        searchInput.current.value = '';
        setResults(null);
        clearResults();
    });

    const onAutocompleteSelect = (keyword, options) => {
        setLatestSearch(keyword);
        search(keyword, productGroups, options);
        setResults([]);
    };

    useEffect(() => {
        if (defaultSearch) {
            searchInput.current.value = defaultSearch;
        }
    }, [searchInput]);

    useEffect(() => {
        if (!query) {
            return;
        }

        clearResults();

        searchApi.autocomplete(query, clientData, { Type: 'Product', ProductCount: autocompleteCount }).then(resp => {
            const { data } = resp;
            const reqData = JSON.parse(resp?.config?.data);
            if (reqData.Keyword !== searchInput.current.value) {
                return [];
            }

            autoCompleteAction(data, query, onAutocompleteSelect, setResults);
        });
    }, [query]);

    return (
        <div className="suppliesSearch">
            <div className="suppliesSearch-icon-container">
                <div className="suppliesSearch-cross-icon">
                    {isLoading ? (
                        <CircleLoader className="suppliesSearch-loader" size="30px" />
                    ) : (
                        <Cross
                            style={query ? { display: 'block' } : { display: 'none' }}
                            onClick={() => onClearSearch(null)}
                        />
                    )}
                </div>

                <Search className="suppliesSearch-search-icon" />
            </div>
            <Typography variant="bodyM">
                <input
                    type="text"
                    ref={searchInput}
                    className="suppliesSearch-input"
                    placeholder="Search by printer name, model, or cartridge #"
                    onFocus={e => {
                        if (!e.target.value) e.target.value = '';
                    }}
                    onChange={e => debouncedSearch(e.target.value)}
                />

                <AutocompleteContainer
                    results={results}
                    onAutocompleteSelect={onAutocompleteSelect}
                    searchTerm={query}
                />
            </Typography>
        </div>
    );
}
